import { ProjectAction, ProjectActionTypes } from '@completion/actions';
import { byName, Project, ProjectConfig, TreeNode, TreeNodeType } from '@completion/models';
import { PackageType } from '@completion/services';

// ── Tree index ──────────────────────────────────────────────────────────────

/**
 * Pre-computed lookup maps built from the raw treeNodes every time the tree
 * is loaded.  Stored in the NgRx state so any consumer can select it without
 * re-deriving it on every change-detection cycle.
 */
export interface TreeIndex {
  /** Every node in the tree keyed by its id */
  byId: Record<number, TreeNode>;
  /** Structural parent-id keyed by child-id (derived from tree shape, NOT node.parentId) */
  parentOf: Record<number, number>;
  /** Per-task hierarchy: taskId → systemId → subsystemId → cpId[] */
  taskMap: Record<number, Record<number, Record<number, number[]>>>;
  /**
   * "No-task" global map (all tasks merged):
   * systemId → subsystemId → cpId[]
   * Useful for showing the whole combined tree without the task level.
   */
  globalMap: Record<number, Record<number, number[]>>;
  /** Quick lookup for CP siblings: subsystemId → cpId[] (all tasks merged) */
  cpsBySubsystem: Record<number, number[]>;
  /** cpId → mcpId[] */
  mcpMap: Record<number, number[]>;
}

export const emptyTreeIndex: TreeIndex = {
  byId: {},
  parentOf: {},
  taskMap: {},
  globalMap: {},
  cpsBySubsystem: {},
  mcpMap: {},
};

export function buildTreeIndex(roots: TreeNode[]): TreeIndex {
  const byId: Record<number, TreeNode> = {};
  const parentOf: Record<number, number> = {};
  const taskMap: Record<number, Record<number, Record<number, number[]>>> = {};
  const globalMap: Record<number, Record<number, number[]>> = {};
  const cpsBySubsystem: Record<number, number[]> = {};
  const mcpMap: Record<number, number[]> = {};

  for (const taskNode of roots) {
    byId[taskNode.id] = taskNode;
    taskMap[taskNode.id] = {};

    for (const systemNode of taskNode.children ?? []) {
      byId[systemNode.id] = systemNode;
      parentOf[systemNode.id] = taskNode.id;
      taskMap[taskNode.id][systemNode.id] = {};

      if (!globalMap[systemNode.id]) {
        globalMap[systemNode.id] = {};
      }

      for (const subsystemNode of systemNode.children ?? []) {
        byId[subsystemNode.id] = subsystemNode;
        parentOf[subsystemNode.id] = systemNode.id;
        taskMap[taskNode.id][systemNode.id][subsystemNode.id] = [];

        if (!globalMap[systemNode.id][subsystemNode.id]) {
          globalMap[systemNode.id][subsystemNode.id] = [];
        }
        if (!cpsBySubsystem[subsystemNode.id]) {
          cpsBySubsystem[subsystemNode.id] = [];
        }

        for (const cpNode of subsystemNode.children ?? []) {
          byId[cpNode.id] = cpNode;
          parentOf[cpNode.id] = subsystemNode.id;
          taskMap[taskNode.id][systemNode.id][subsystemNode.id].push(cpNode.id);

          // Merge into global maps (dedup — same CP can appear under multiple tasks)
          if (!globalMap[systemNode.id][subsystemNode.id].includes(cpNode.id)) {
            globalMap[systemNode.id][subsystemNode.id].push(cpNode.id);
          }
          if (!cpsBySubsystem[subsystemNode.id].includes(cpNode.id)) {
            cpsBySubsystem[subsystemNode.id].push(cpNode.id);
          }

          mcpMap[cpNode.id] = [];
          for (const mcpNode of cpNode.children ?? []) {
            byId[mcpNode.id] = mcpNode;
            parentOf[mcpNode.id] = cpNode.id;
            mcpMap[cpNode.id].push(mcpNode.id);
          }
        }
      }
    }
  }

  return { byId, parentOf, taskMap, globalMap, cpsBySubsystem, mcpMap };
}

// ── State ───────────────────────────────────────────────────────────────────

export const initialState: ProjectState = {
  projects: [],
  currentProject: null,
  treeNodes: [],
  treeIndex: emptyTreeIndex,
  currentProjectConfigs: [],
  heatTraceConfigured: null,
  loading: false,
};

export interface ProjectState {
  projects: Project[];
  currentProject: Project;
  treeNodes: TreeNode[];
  /** Pre-computed lookup maps for fast tree navigation (e.g. breadcrumb) */
  treeIndex: TreeIndex;
  currentProjectConfigs: ProjectConfig[];
  heatTraceConfigured: boolean | null;
  loading: boolean;
}

export function projectReducer(state: ProjectState = initialState, action: ProjectAction): ProjectState {
  switch (action.type) {
    case ProjectActionTypes.ProjectsLoad:
      return { ...state, loading: true };

    case ProjectActionTypes.ProjectsLoadSuccess:
      return {
        ...state,
        projects: [...action.projects].sort(byName),
        currentProject: action.currentProject ?? null,
        currentProjectConfigs: action.currentProject?.config ?? [],
        heatTraceConfigured: null,
        loading: false,
      };
    case ProjectActionTypes.ChangeProject:
      return {
        ...state,
        currentProject: action.project,
        currentProjectConfigs: action.project.config,
        heatTraceConfigured: null,
      };
    case ProjectActionTypes.HeatTraceConfiguredLoadSuccess:
      return {
        ...state,
        heatTraceConfigured: action.heatTraceConfigured,
      };
    case ProjectActionTypes.ProjectTreeLoadSuccess:
      return {
        ...state,
        treeNodes: action.treeNodes,
        treeIndex: buildTreeIndex(action.treeNodes),
      };
    case ProjectActionTypes.ProjectConfigLoadSuccess:
      const updatedProject = state.currentProject ? { ...state.currentProject, config: action.config } : state.currentProject;
      return {
        ...state,
        currentProjectConfigs: action.config,
        currentProject: updatedProject,
      };
    case ProjectActionTypes.SignatureMatrixTemplateLoadSuccess:
      const project = deepCopy(state.currentProject);
      project.signatureMatrixTemplates = action.signatureMatrices;
      return {
        ...state,
        currentProject: project,
      };
    case ProjectActionTypes.ProjectTreeUpdateNode:
      const tree = deepCopy(state.treeNodes);
      let isDirty = false;
      tree.forEach((node) => {
        const n = searchTreeByNo(
          node,
          action.update.packageNumber,
          action.update.packageType === PackageType.MCP ? TreeNodeType.MCP : TreeNodeType.CP,
        );
        if (n && n.status !== action.update.status) {
          n.status = action.update.status;
          isDirty = true;
        }
      });
      if (!isDirty) {
        return {
          ...state,
          treeNodes: tree,
        };
      }
    case ProjectActionTypes.ProjectsLoadFailure:
      return { ...state, loading: false };

    default:
      return state;
  }
}

export function searchTreeByNo(node: TreeNode, compare: string, nodeType: TreeNodeType): any {
  if (node.number === compare && node.nodeType === nodeType) {
    return node;
  } else if (node.children != null) {
    let result = null;
    for (let i = 0; result == null && i < node.children.length; i++) {
      result = searchTreeByNo(node.children[i], compare, nodeType);
    }
    return result;
  }
  return null;
}

export function deepCopy(obj): any {
  let copy;

  // Handle the 3 simple types, and null or undefined
  if (null == obj || 'object' != typeof obj) return obj;

  // Handle Array
  if (obj instanceof Array) {
    copy = [];
    for (let i = 0, len = obj.length; i < len; i++) {
      copy[i] = deepCopy(obj[i]);
    }
    return copy;
  }

  // Handle Object
  if (obj instanceof Object) {
    copy = {};
    for (const attr in obj) {
      if (obj.hasOwnProperty(attr)) copy[attr] = deepCopy(obj[attr]);
    }
    return copy;
  }

  throw new Error("Unable to copy obj! Its type isn't supported.");
}
